import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { IGetRestaurantsShort, IGetRestaurantsShortArgs, EntityStatus } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { ISelectOption } from 'components/Select/types';
import FormSelect from 'components/Select/FormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import { TCreateDeviceSelectedItem } from 'modules/Devices/CreateDevice/createDeviceState/createDeviceContext';
import { getRestaurantsShortInfo } from 'backend/restaurants';

interface IRestaurantField {
	setIDRestaurant: (value: TCreateDeviceSelectedItem) => void;
	IDSelectedCustomer: TCreateDeviceSelectedItem;
	IDSelectedRestaurant: TCreateDeviceSelectedItem;
}

export const RestaurantField: React.FC<IRestaurantField> = React.memo(
	({ IDSelectedCustomer, setIDRestaurant, IDSelectedRestaurant }) => {
		const { response, loading, refetch, responseError } = useFetchData<
			IGetRestaurantsShort,
			IGetRestaurantsShortArgs
		>(IDSelectedCustomer ? getRestaurantsShortInfo : null, {
			customerId: IDSelectedCustomer || '',
		});
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
		);

		useEffect(() => {
			refetch();
		}, [IDSelectedCustomer]);

		const options = useMemo<ISelectOption[]>(() => {
			const items = response?.data.restaurants || [];
			const visibleItems = showArchived
				? items
				: items.filter(
						(restaurant) =>
							restaurant.status === EntityStatus.Active || restaurant.id === IDSelectedRestaurant,
				  );

			return visibleItems.map((restaurant) => ({
				...restaurant,
				additionalContent: restaurant.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [response, showArchived, IDSelectedRestaurant]);

		const toggleShowArchived = useCallback(() => {
			localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		}, [showArchived]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => (
				<DropdownRender menu={menu}>
					<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
				</DropdownRender>
			),
			[showArchived, toggleShowArchived],
		);

		return (
			<FormSelect
				testId="RestaurantField"
				name="IDSelectedRestaurant"
				label="restaurantLabel"
				options={options}
				fetchError={responseError}
				loading={loading}
				onRefetch={refetch}
				disabled={!IDSelectedCustomer}
				onChange={setIDRestaurant}
				keepSearchWithDropdownRender={true}
				dropdownRender={renderSelectMenu}
			/>
		);
	},
);
